import React, { useEffect, useRef, useState } from 'react';
import { Switch } from '@givebutter/primitives/switch';
import * as Tooltip from '@givebutter/primitives/tooltip';
import {
  faChevronsLeft,
  faDisplaySlash,
  faLink,
  faMaximize,
  faMinimize,
  faPalette,
  faSliders,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import givebutterLogoUrl from 'assets/img/givebutter-logo.svg';
import { useCopy } from 'hooks/useCopy';
import { LiveDisplaySettings } from 'hooks/useStore';
import screenfull from 'screenfull';
import { ThemeName, Themes } from 'types/theme';

interface SettingsEditorProps {
  hasTextToDonate: boolean;
  isMouseMoving: boolean;
  isMenuVisible: boolean;
  themes: Themes;
  liveDisplaySettings: LiveDisplaySettings;
  liveDisplayUrl: string;
  campaignUrl: string;
  setIsMenuVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setTheme: (themeName: ThemeName) => void;
  setLiveDisplaySettings: (liveDisplaySettings: LiveDisplaySettings) => void;
}

const SettingsEditor = ({
  isMouseMoving,
  isMenuVisible,
  hasTextToDonate,
  themes,
  liveDisplaySettings,
  campaignUrl,
  liveDisplayUrl,
  setIsMenuVisible,
  setTheme,
  setLiveDisplaySettings,
}: SettingsEditorProps) => {
  const settingsRef = useRef<HTMLDivElement>(null);
  const { didCopy, onClickCopy } = useCopy();
  const [isFullscreen, setIsFullscreen] = useState(screenfull.isFullscreen);
  const [isMouseHovering, setIsMouseHovering] = useState(false);
  const handleClickOutside = (e: MouseEvent) => {
    if (settingsRef !== null && !settingsRef?.current?.contains(e.target as Node)) {
      setIsMenuVisible(false);
    }
  };

  useEffect(() => {
    if (!isMouseHovering && !isMouseMoving && isMenuVisible) {
      setIsMenuVisible(false);
    }
  }, [isMouseMoving, isMouseHovering, isMenuVisible, setIsMenuVisible]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  });

  const renderThemeButtons = Object.keys(themes).map(theme => {
    return (
      <button
        key={theme}
        className={`mb-3 block w-full rounded-full border-2 border-gray-200 px-4 py-2 capitalize hover:bg-black hover:font-semibold hover:text-white ${
          liveDisplaySettings.theme.name === theme
            ? 'bg-black font-semibold text-white'
            : 'bg-white text-black'
        }`}
        onClick={() => {
          setTheme(theme as ThemeName);
        }}
      >
        {theme}
      </button>
    );
  });

  return (
    <div
      ref={settingsRef}
      onMouseEnter={() => {
        setIsMouseHovering(true);
      }}
      onMouseLeave={() => {
        setIsMouseHovering(false);
      }}
      className={`container absolute bottom-0 left-0 top-0 z-50 w-[27.625rem] origin-left overflow-y-auto overflow-x-hidden bg-gray-50 p-8 text-2xl text-black shadow-2xl transition-all duration-500 ${
        !isMenuVisible ? 'left-[-27.625rem]' : ''
      }`}
    >
      <div className="mb-12 flex items-center justify-between">
        <img src={givebutterLogoUrl} className="h-14" alt="Givebutter logo" />
        <button
          className={`pointer`}
          onClick={() => {
            setIsMenuVisible(false);
          }}
        >
          <FontAwesomeIcon icon={faChevronsLeft} />
        </button>
      </div>

      <div className={'flex justify-between font-semibold'}>
        Configuration <FontAwesomeIcon icon={faSliders} />
      </div>

      <hr className="mb-4 mt-2" />

      <SettingToggleSwitch
        label="Amount Raised"
        checked={liveDisplaySettings.showAmountRaised}
        onClick={() => {
          setLiveDisplaySettings({
            ...liveDisplaySettings,
            showAmountRaised: !liveDisplaySettings.showAmountRaised,
          });
        }}
      />

      <SettingToggleSwitch
        label="Supporter Feed"
        checked={liveDisplaySettings.showShoutouts}
        onClick={() => {
          setLiveDisplaySettings({
            ...liveDisplaySettings,
            showShoutouts: !liveDisplaySettings.showShoutouts,
          });
        }}
      />

      <SettingToggleSwitch
        label="Scan-to-Donate"
        checked={liveDisplaySettings.showQRCode}
        onClick={() => {
          setLiveDisplaySettings({
            ...liveDisplaySettings,
            showQRCode: !liveDisplaySettings.showQRCode,
          });
        }}
      />

      <SettingToggleSwitch
        label="Text-to-Donate"
        disabled={!hasTextToDonate}
        checked={hasTextToDonate && liveDisplaySettings.showSMSKeyword}
        tooltipContent={
          <>
            To use this feature within Live Display, please set a Text-to-Donate keyword in your
            account settings.{' '}
            <a
              href="https://help.givebutter.com/en/articles/4044807-how-to-configure-text-to-give"
              target="_blank"
              rel="noreferrer"
              className={`underline`}
            >
              Set up keyword.
            </a>
          </>
        }
        onClick={() => {
          setLiveDisplaySettings({
            ...liveDisplaySettings,
            showSMSKeyword: !liveDisplaySettings.showSMSKeyword,
          });
        }}
      />

      <div className={'mt-12 flex justify-between font-semibold'}>
        Theme <FontAwesomeIcon icon={faPalette} />
      </div>

      <hr className="mb-4 mt-2" />

      <div>{renderThemeButtons}</div>

      <div className={'mt-12 flex justify-between font-semibold'}>
        Live Display URL <FontAwesomeIcon icon={faLink} />
      </div>

      <hr className="mb-4 mt-2" />

      <div className={`relative`}>
        <input
          readOnly
          className={`w-full rounded-xl border border-gray-300 p-2 text-lg text-gray-500`}
          type="text"
          value={liveDisplayUrl}
        />
        <span className="absolute right-0 top-0 rounded-xl p-1">
          <button
            className={`rounded-xl px-4 py-2 text-sm font-semibold text-white ${
              didCopy ? 'bg-green-500' : 'bg-blue-500'
            }`}
            onClick={e => {
              onClickCopy(e, liveDisplayUrl);
            }}
          >
            {didCopy ? 'Copied!' : 'Copy'}
          </button>
        </span>
      </div>

      {screenfull.isEnabled && (
        <button
          onClick={() => {
            if (isFullscreen) {
              screenfull.exit();
              setIsFullscreen(false);
            } else {
              screenfull.request();
              setIsFullscreen(true);
            }
          }}
          className={`pointer mt-16 block w-full rounded border border-gray-300 bg-white p-[6px_14px] px-4 py-2 text-lg font-semibold`}
        >
          <FontAwesomeIcon icon={isFullscreen ? faMinimize : faMaximize} />
          &nbsp;{isFullscreen ? 'Exit' : 'Enter'} Fullscreen
        </button>
      )}

      <a
        href={campaignUrl}
        className={`pointer mt-4 block w-full rounded border border-gray-300 bg-white p-[6px_14px] px-4 py-2 text-center text-lg font-semibold text-red-500`}
      >
        <FontAwesomeIcon icon={faDisplaySlash} />
        &nbsp;Close Live Display
      </a>
    </div>
  );
};

interface SettingToggleSwitchProps {
  label: string;
  checked: boolean;
  tooltipContent?: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}
const SettingToggleSwitch = ({
  label,
  checked,
  disabled,
  tooltipContent,
  onClick,
}: SettingToggleSwitchProps) => (
  <div className={'mb-4'}>
    <label
      className={`relative flex items-center justify-between ${disabled ? 'text-gray-300' : ''}`}
    >
      {label}
      <Tooltip.Root>
        <div className={`relative`}>
          {disabled && (
            <Tooltip.Trigger asChild>
              <span className="absolute inset-0 z-40"></span>
            </Tooltip.Trigger>
          )}
          <Switch
            disabled={disabled}
            defaultChecked={checked}
            size={'lg'}
            onCheckedChange={onClick}
          />
          <Tooltip.Portal>
            <Tooltip.Content>
              {tooltipContent}
              <Tooltip.Arrow />
            </Tooltip.Content>
          </Tooltip.Portal>
        </div>
      </Tooltip.Root>
    </label>
  </div>
);

export { SettingsEditor };
